import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import {
  Calendar,
  CalendarDays,
  Download,
  Edit,
  GripVertical,
  List,
  Package,
  PanelRightClose,
  PanelRightOpen,
  Plus,
  Printer,
  Save,
  Search,
  Trash2,
  UserPlus,
  Users,
  X
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import secureStorage from '../utils/secureStorage';
import { writeSanitizedHTML } from '../utils/safePrint';
import MeetingCalendarView from './MeetingCalendarView';

// ICS section categories with sidebar colors
export const MEETING_CATEGORIES = [
  { id: '', label: 'None', hex: '', dotClass: 'bg-gray-700' },
  { id: 'command-staff', label: 'Command Staff', hex: '#b91c1c', dotClass: 'bg-red-700' },
  { id: 'operations', label: 'Operations', hex: '#f97316', dotClass: 'bg-orange-500' },
  { id: 'planning', label: 'Planning', hex: '#3b82f6', dotClass: 'bg-blue-500' },
  { id: 'logistics', label: 'Logistics', hex: '#f59e0b', dotClass: 'bg-amber-500' },
  { id: 'fin-admin', label: 'Fin / Admin', hex: '#6b7280', dotClass: 'bg-gray-500' }
];

export const getCategory = (id) =>
  MEETING_CATEGORIES.find(c => c.id === (id || '')) || MEETING_CATEGORIES[0];

const getAuthHeaders = () => {
  const token = secureStorage.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getParticipantName = (p) => {
  if (!p) return '';
  return p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Unnamed';
};

const API = `${API_BASE_URL}/api`;

const ICS230Form = ({ currentExercise, theme }) => {
  const [view, setView] = useState('list');
  const [listMode, setListMode] = useState('list'); // 'list' | 'calendar'
  const [schedules, setSchedules] = useState([]);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [scrollToMeetingId, setScrollToMeetingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    exercise_name: '',
    operational_period_from: '',
    operational_period_to: '',
    meetings: [
      {
        id: Date.now(),
        date_time: '',
        date_time_to: '',
        meeting_name: '',
        purpose: '',
        attendees: '',
        location: '',
        category: ''
      }
    ],
    prepared_by: '',
    prepared_date_time: ''
  });

  const API = API_BASE_URL || 'http://localhost:8001';

  // Fetch meeting schedules
  useEffect(() => {
    const fetchSchedules = async () => {
      if (currentExercise?.id) {
        try {
          const response = await axios.get(`${API}/api/meeting-schedules?exercise_id=${currentExercise.id}`, { headers: getAuthHeaders() });
          setSchedules(response.data);
        } catch (error) {
          console.error('Error fetching meeting schedules:', error);
        }
      }
    };
    fetchSchedules();
  }, [currentExercise, API]);

  // Set exercise name when currentExercise changes
  useEffect(() => {
    if (currentExercise?.exercise_name) {
      setFormData(prev => ({
        ...prev,
        exercise_name: currentExercise.exercise_name
      }));
    }
  }, [currentExercise]);

  // Scroll to specific meeting when entering edit from calendar
  useEffect(() => {
    if (view === 'add' && scrollToMeetingId) {
      const t = setTimeout(() => {
        const el = document.querySelector(`[data-meeting-id="${scrollToMeetingId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('ring-2', 'ring-orange-500/60');
          setTimeout(() => el.classList.remove('ring-2', 'ring-orange-500/60'), 2000);
        }
        setScrollToMeetingId(null);
      }, 200);
      return () => clearTimeout(t);
    }
  }, [view, scrollToMeetingId]);

  // Quick Assign state
  const [participants, setParticipants] = useState([]);
  const [resources, setResources] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarTab, setSidebarTab] = useState('personnel');
  const [searchQuery, setSearchQuery] = useState('');
  const [dropTargetIndex, setDropTargetIndex] = useState(null);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  const showToast = useCallback((message, type = 'success') => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ message, type });
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  }, []);

  // Fetch participants for Quick Assign
  useEffect(() => {
    const fetchParticipants = async () => {
      if (!currentExercise?.id) return;
      try {
        const headers = getAuthHeaders();
        const response = await axios.get(
          `${API_BASE_URL}/api/exercises/${currentExercise.id}/participants`,
          { headers }
        );
        setParticipants(response.data || []);
      } catch (error) {
        console.error('Error fetching participants:', error);
      }
    };
    fetchParticipants();
  }, [currentExercise?.id]);

  // Fetch resource units for Quick Assign (same pattern as Task Forces)
  useEffect(() => {
    const fetchResources = async () => {
      if (!currentExercise?.id) return;
      try {
        const headers = getAuthHeaders();
        const response = await axios.get(
          `${API_BASE_URL}/api/resources?exercise_id=${currentExercise.id}`,
          { headers }
        );
        const allResources = response.data || [];
        const allUnits = [];
        for (const res of allResources) {
          try {
            const unitsRes = await axios.get(
              `${API_BASE_URL}/api/resources/${res.id}/units`,
              { headers }
            );
            (unitsRes.data || []).forEach(u =>
              allUnits.push({ ...u, resource_type: res.resource_type, parent_resource_id: res.id })
            );
          } catch { /* skip */ }
        }
        setResources(allUnits);
      } catch (error) {
        console.error('Error fetching resources:', error);
      }
    };
    fetchResources();
  }, [currentExercise?.id]);

  // Drag handlers (same pattern as Task Forces)
  const onDragStart = (e, item, itemType) => {
    e.dataTransfer.setData('item_id', item.id);
    e.dataTransfer.setData('item_type', itemType);
    const label = itemType === 'resource'
      ? (item.unit_name || item.resource_type || 'Unit') +
        (item.resource_type && item.unit_name ? ` (${item.resource_type})` : '')
      : getParticipantName(item);
    e.dataTransfer.setData('item_label', label);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const onDragOver = (e, meetingIndex) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDropTargetIndex(meetingIndex);
  };

  const onDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setDropTargetIndex(null);
  };

  const appendToMeetingAttendees = (meetingIndex, label) => {
    if (!label) return { added: false };
    let added = true;
    setFormData(prev => ({
      ...prev,
      meetings: prev.meetings.map((m, i) => {
        if (i !== meetingIndex) return m;
        const existing = (m.attendees || '').trim();
        const names = existing
          ? existing.split(/\r?\n|,/).map(s => s.trim()).filter(Boolean)
          : [];
        if (names.some(n => n.toLowerCase() === label.toLowerCase())) {
          added = false;
          return m;
        }
        names.push(label);
        return { ...m, attendees: names.join('\n') };
      })
    }));
    return { added };
  };

  const onDrop = (e, meetingIndex) => {
    e.preventDefault();
    const itemType = e.dataTransfer.getData('item_type');
    const label = e.dataTransfer.getData('item_label');
    if (label) {
      const meeting = formData.meetings[meetingIndex];
      const existing = (meeting?.attendees || '').trim();
      const already = existing
        .split(/\r?\n|,/)
        .map(s => s.trim().toLowerCase())
        .includes(label.toLowerCase());
      appendToMeetingAttendees(meetingIndex, label);
      const kind = itemType === 'resource' ? 'Resource' : 'Personnel';
      showToast(
        already
          ? `${label} is already in Attendees`
          : `${kind} "${label}" added to Meeting ${meetingIndex + 1}`,
        already ? 'error' : 'success'
      );
    }
    setDropTargetIndex(null);
  };

  const filteredParticipants = participants.filter(p => {
    if (!searchQuery) return true;
    const name = getParticipantName(p).toLowerCase();
    const role = (p.position || p.role || '').toLowerCase();
    return name.includes(searchQuery.toLowerCase()) || role.includes(searchQuery.toLowerCase());
  });

  const filteredResources = resources.filter(r => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (r.unit_name || '').toLowerCase().includes(q)
      || (r.resource_type || '').toLowerCase().includes(q)
      || (r.status || '').toLowerCase().includes(q);
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMeetingChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      meetings: prev.meetings.map((meeting, i) => 
        i === index ? { ...meeting, [field]: value } : meeting
      )
    }));
  };

  const addMeeting = () => {
    setFormData(prev => ({
      ...prev,
      meetings: [
        ...prev.meetings,
        {
          id: Date.now() + Math.random(),
          date_time: '',
          date_time_to: '',
          meeting_name: '',
          purpose: '',
          attendees: '',
          location: '',
          category: ''
        }
      ]
    }));
  };

  const removeMeeting = (index) => {
    if (formData.meetings.length > 1) {
      setFormData(prev => ({
        ...prev,
        meetings: prev.meetings.filter((_, i) => i !== index)
      }));
    }
  };

  const resetForm = () => {
    setFormData({
      exercise_name: currentExercise?.exercise_name || '',
      operational_period_from: '',
      operational_period_to: '',
      meetings: [
        {
          id: Date.now(),
          date_time: '',
          date_time_to: '',
          meeting_name: '',
          purpose: '',
          attendees: '',
          location: '',
          category: ''
        }
      ],
      prepared_by: '',
      prepared_date_time: ''
    });
  };

  const handleSave = async () => {
    const scheduleData = {
      id: editingSchedule ? editingSchedule.id : Date.now(),
      ...formData,
      exercise_id: currentExercise?.id,
      created_at: editingSchedule ? editingSchedule.created_at : new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    setLoading(true);
    try {
      if (editingSchedule) {
        await axios.put(`${API}/api/meeting-schedules/${editingSchedule.id}`, scheduleData, { headers: getAuthHeaders() });
        setSchedules(prev => prev.map(item => item.id === editingSchedule.id ? scheduleData : item));
      } else {
        const response = await axios.post(`${API}/api/meeting-schedules`, scheduleData, { headers: getAuthHeaders() });
        setSchedules(prev => [...prev, response.data]);
      }
      setView('list');
      setEditingSchedule(null);
      resetForm();
    } catch (error) {
      console.error('Error saving meeting schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveEntry = handleSave;

  const handleEdit = (schedule, meetingId = null) => {
    setEditingSchedule(schedule);
    setFormData(schedule);
    setScrollToMeetingId(meetingId);
    setView('add');
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/api/meeting-schedules/${id}`, { headers: getAuthHeaders() });
      setSchedules(prev => prev.filter(item => item.id !== id));
    } catch (error) {
      console.error('Error deleting meeting schedule:', error);
    }
  };

  // Calendar: click empty slot -> create new schedule with one meeting prefilled at that date/time
  const handleCalendarSelectSlot = (dateTimeStr) => {
    setEditingSchedule(null);
    setFormData({
      exercise_name: currentExercise?.exercise_name || '',
      operational_period_from: dateTimeStr.split('T')[0],
      operational_period_to: dateTimeStr.split('T')[0],
      meetings: [
        {
          id: Date.now(),
          date_time: dateTimeStr,
          date_time_to: '',
          meeting_name: '',
          purpose: '',
          attendees: '',
          location: '',
          category: ''
        }
      ],
      prepared_by: '',
      prepared_date_time: ''
    });
    setView('add');
  };

  // Calendar: click event -> open the schedule for editing, scroll to that meeting
  const handleCalendarSelectEvent = (event) => {
    const schedule = schedules.find(s => String(s.id) === String(event.scheduleId));
    if (!schedule) return;
    handleEdit(schedule, event.meetingId);
  };

  // Calendar: drag-drop event -> shift date_time (and date_time_to by the same delta if present), then PUT
  const handleCalendarEventDrop = async (event, newDateTime) => {
    const schedule = schedules.find(s => String(s.id) === String(event.scheduleId));
    if (!schedule) return;
    // Compute delta in minutes between old and new start
    const oldStart = new Date((schedule.meetings || []).find(m => String(m.id) === String(event.meetingId))?.date_time || newDateTime);
    const newStart = new Date(newDateTime);
    const deltaMs = (isNaN(oldStart.getTime()) || isNaN(newStart.getTime()))
      ? 0
      : newStart.getTime() - oldStart.getTime();

    const shiftDt = (dtStr) => {
      if (!dtStr) return '';
      const d = new Date(dtStr);
      if (isNaN(d.getTime())) return dtStr;
      const shifted = new Date(d.getTime() + deltaMs);
      const pad = (n) => String(n).padStart(2, '0');
      return `${shifted.getFullYear()}-${pad(shifted.getMonth() + 1)}-${pad(shifted.getDate())}T${pad(shifted.getHours())}:${pad(shifted.getMinutes())}`;
    };

    const updatedMeetings = (schedule.meetings || []).map(m => {
      if (String(m.id) !== String(event.meetingId)) return m;
      return {
        ...m,
        date_time: newDateTime,
        date_time_to: m.date_time_to ? shiftDt(m.date_time_to) : ''
      };
    });
    const updated = { ...schedule, meetings: updatedMeetings, updated_at: new Date().toISOString() };
    setSchedules(prev => prev.map(s => s.id === schedule.id ? updated : s));
    try {
      await axios.put(`${API}/api/meeting-schedules/${schedule.id}`, updated, { headers: getAuthHeaders() });
    } catch (error) {
      console.error('Error rescheduling meeting:', error);
      // rollback on error
      setSchedules(prev => prev.map(s => s.id === schedule.id ? schedule : s));
    }
  };

  // iCal export: download .ics file (one schedule or all schedules for this exercise)
  const downloadIcal = async (scheduleId = null) => {
    try {
      const url = scheduleId
        ? `${API}/api/meeting-schedules/${scheduleId}/export.ics`
        : `${API}/api/meeting-schedules/export.ics?exercise_id=${currentExercise?.id || ''}`;
      const response = await axios.get(url, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'text/calendar;charset=utf-8' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = scheduleId
        ? `exrsim-meetings-${scheduleId}.ics`
        : `exrsim-meetings-${currentExercise?.exercise_name || 'all'}.ics`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Error downloading iCal:', error);
      alert('Failed to download calendar file. Please try again.');
    }
  };

  // Per-meeting Google Calendar deep link
  const googleCalendarLinkForMeeting = (meeting) => {
    if (!meeting?.date_time) return null;
    const formatGoogle = (dt) => {
      try {
        const [d, t] = dt.split('T');
        const time = (t || '00:00').slice(0, 5).replace(':', '');
        return `${d.replaceAll('-', '')}T${time}00`;
      } catch {
        return null;
      }
    };
    const start = formatGoogle(meeting.date_time);
    if (!start) return null;
    // Use date_time_to if present and valid, else default +1h
    let endStr = null;
    if (meeting.date_time_to) {
      endStr = formatGoogle(meeting.date_time_to);
    }
    if (!endStr) {
      const startDate = new Date(meeting.date_time);
      if (isNaN(startDate.getTime())) return null;
      const end = new Date(startDate.getTime() + 60 * 60 * 1000);
      const pad = (n) => String(n).padStart(2, '0');
      endStr = `${end.getFullYear()}${pad(end.getMonth() + 1)}${pad(end.getDate())}T${pad(end.getHours())}${pad(end.getMinutes())}00`;
    }
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: meeting.meeting_name || 'Meeting',
      dates: `${start}/${endStr}`,
      details: [meeting.purpose, meeting.attendees ? `Attendees:\n${meeting.attendees}` : ''].filter(Boolean).join('\n\n'),
      location: meeting.location || ''
    });
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  };

  const printICS230 = (scheduleOverride = null) => {
    // Priority: explicit schedule parameter > editingSchedule > formData (for create/edit view)
    const scheduleToPrint = scheduleOverride || editingSchedule || formData;
    
    // Validate we have data to print
    if (!scheduleToPrint || (!scheduleToPrint.meetings || scheduleToPrint.meetings.length === 0)) {
      alert('No meeting schedule data to print. Please create meetings first.');
      return;
    }

    // Log the number of meetings being printed
    console.log(`Printing ICS 230 with ${scheduleToPrint.meetings.length} meetings:`, scheduleToPrint.meetings);
    
    const printContent = `
      <html>
        <head>
          <title>ICS 230 - Daily Meeting Schedule</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; }
            .header { text-align: center; margin-bottom: 20px; }
            .form-section { margin-bottom: 15px; }
            .form-row { display: flex; gap: 20px; margin-bottom: 8px; align-items: center; }
            .form-field { flex: 1; }
            .form-field-small { flex: 0.5; }
            .label { font-weight: bold; margin-bottom: 3px; font-size: 11px; }
            .value { border-bottom: 1px solid #000; min-height: 18px; padding: 2px; font-size: 11px; }
            .section-title { color: #000; font-weight: bold; font-size: 14px; margin: 15px 0 10px 0; }
            .meeting-table { width: 100%; border-collapse: collapse; margin: 10px 0; }
            .meeting-table th, .meeting-table td { border: 1px solid #000; padding: 8px; text-align: left; font-size: 10px; }
            .meeting-table th { background-color: #f5f5f5; font-weight: bold; }
            .textarea-content { min-height: 40px; vertical-align: top; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>ICS 230 - DAILY MEETING SCHEDULE</h1>
          </div>
          
          <div class="form-section">
            <div class="form-row">
              <div class="form-field">
                <div class="label">Exercise Name:</div>
                <div class="value">${scheduleToPrint.exercise_name || ''}</div>
              </div>
              <div class="form-field">
                <div class="label">Operational Period:</div>
                <div style="display: flex; gap: 10px; align-items: center;">
                  <span style="font-size: 10px;">From:</span>
                  <span class="value" style="flex: 1;">${scheduleToPrint.operational_period_from || ''}</span>
                  <span style="font-size: 10px;">To:</span>
                  <span class="value" style="flex: 1;">${scheduleToPrint.operational_period_to || ''}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="section-title">Meeting Schedule</div>
          
          <table class="meeting-table">
            <thead>
              <tr>
                <th style="width: 12%;">Date/Time From</th>
                <th style="width: 12%;">Date/Time To</th>
                <th style="width: 18%;">Meeting Name</th>
                <th style="width: 22%;">Purpose</th>
                <th style="width: 22%;">Attendees</th>
                <th style="width: 14%;">Location</th>
              </tr>
            </thead>
            <tbody>
              ${(scheduleToPrint.meetings || []).map(meeting => {
                const cat = MEETING_CATEGORIES.find(c => c.id === (meeting.category || ''));
                const accentStyle = cat && cat.hex ? ` style="border-left: 4px solid ${cat.hex};"` : '';
                return `
                <tr${accentStyle}>
                  <td>${meeting.date_time || ''}</td>
                  <td>${meeting.date_time_to || ''}</td>
                  <td>${meeting.meeting_name || ''}</td>
                  <td class="textarea-content">${meeting.purpose || ''}</td>
                  <td class="textarea-content">${(meeting.attendees || '').replace(/\r?\n/g, '<br/>')}</td>
                  <td>${meeting.location || ''}${cat && cat.id ? `<br/><span style="font-size:9px;color:${cat.hex};">${cat.label}</span>` : ''}</td>
                </tr>
              `;
              }).join('')}
            </tbody>
          </table>
          
          <div style="margin: 30px 0;">
            <div class="form-row">
              <div class="form-field">
                <div class="label">Prepared by:</div>
                <div class="value">${scheduleToPrint.prepared_by || ''}</div>
              </div>
              <div class="form-field">
                <div class="label">Date/Time:</div>
                <div class="value">${scheduleToPrint.prepared_date_time || ''}</div>
              </div>
            </div>
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.print();
    printWindow.close();
  };

  const printAllSchedules = () => {
    if (schedules.length === 0) {
      alert('No meeting schedules to print. Please create schedules first.');
      return;
    }

    console.log(`Printing ${schedules.length} ICS 230 schedule(s)`);
    console.log('Full schedules data:', schedules);
    
    // Generate content for all schedules
    const allSchedulesContent = schedules.map((schedule, index) => {
      console.log(`Schedule ${index + 1}:`, schedule);
      console.log(`Schedule ${index + 1} meetings:`, schedule.meetings);
      console.log(`Schedule ${index + 1} meetings length:`, schedule.meetings?.length);
      return `
      ${index > 0 ? '<div style="page-break-before: always;"></div>' : ''}
      <div class="schedule-section">
        <div class="header">
          <h1>ICS 230 - DAILY MEETING SCHEDULE</h1>
          ${schedules.length > 1 ? `<p style="font-size: 12px; color: #666;">Schedule ${index + 1} of ${schedules.length}</p>` : ''}
        </div>
        
        <div class="form-section">
          <div class="form-row">
            <div class="form-field">
              <div class="label">Exercise Name:</div>
              <div class="value">${schedule.exercise_name || ''}</div>
            </div>
            <div class="form-field">
              <div class="label">Operational Period:</div>
              <div style="display: flex; gap: 10px; align-items: center;">
                <span style="font-size: 10px;">From:</span>
                <span class="value" style="flex: 1;">${schedule.operational_period_from || ''}</span>
                <span style="font-size: 10px;">To:</span>
                <span class="value" style="flex: 1;">${schedule.operational_period_to || ''}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="section-title">Meeting Schedule (${schedule.meetings?.length || 0} meeting${schedule.meetings?.length === 1 ? '' : 's'})</div>
        
        <table class="meeting-table">
          <thead>
            <tr>
              <th style="width: 12%;">Date/Time From</th>
              <th style="width: 12%;">Date/Time To</th>
              <th style="width: 18%;">Meeting Name</th>
              <th style="width: 22%;">Purpose</th>
              <th style="width: 22%;">Attendees</th>
              <th style="width: 14%;">Location</th>
            </tr>
          </thead>
          <tbody>
            ${(schedule.meetings || []).map(meeting => {
              const cat = MEETING_CATEGORIES.find(c => c.id === (meeting.category || ''));
              const accentStyle = cat && cat.hex ? ` style="border-left: 4px solid ${cat.hex};"` : '';
              return `
              <tr${accentStyle}>
                <td>${meeting.date_time || ''}</td>
                <td>${meeting.date_time_to || ''}</td>
                <td>${meeting.meeting_name || ''}</td>
                <td class="textarea-content">${meeting.purpose || ''}</td>
                <td class="textarea-content">${(meeting.attendees || '').replace(/\r?\n/g, '<br/>')}</td>
                <td>${meeting.location || ''}${cat && cat.id ? `<br/><span style="font-size:9px;color:${cat.hex};">${cat.label}</span>` : ''}</td>
              </tr>
            `;
            }).join('')}
          </tbody>
        </table>
        
        <div style="margin: 30px 0;">
          <div class="form-row">
            <div class="form-field">
              <div class="label">Prepared by:</div>
              <div class="value">${schedule.prepared_by || ''}</div>
            </div>
            <div class="form-field">
              <div class="label">Date/Time:</div>
              <div class="value">${schedule.prepared_date_time || ''}</div>
            </div>
          </div>
        </div>
      </div>
    `;
    }).join('');
    
    const printContent = `
      <html>
        <head>
          <title>ICS 230 - All Daily Meeting Schedules</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; }
            .header { text-align: center; margin-bottom: 20px; }
            .schedule-section { margin-bottom: 40px; }
            .form-section { margin-bottom: 15px; }
            .form-row { display: flex; gap: 20px; margin-bottom: 8px; align-items: center; }
            .form-field { flex: 1; }
            .form-field-small { flex: 0.5; }
            .label { font-weight: bold; margin-bottom: 3px; font-size: 11px; }
            .value { border-bottom: 1px solid #000; min-height: 18px; padding: 2px; font-size: 11px; }
            .section-title { color: #000; font-weight: bold; font-size: 14px; margin: 15px 0 10px 0; }
            .meeting-table { width: 100%; border-collapse: collapse; margin: 10px 0; }
            .meeting-table th, .meeting-table td { border: 1px solid #000; padding: 8px; text-align: left; font-size: 10px; }
            .meeting-table th { background-color: #f5f5f5; font-weight: bold; }
            .textarea-content { min-height: 40px; vertical-align: top; }
            @media print {
              .page-break { page-break-before: always; }
            }
          </style>
        </head>
        <body>
          ${allSchedulesContent}
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.print();
    printWindow.close();
  };

  if (view === 'list') {
    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className={`text-2xl font-bold ${theme.colors.textPrimary} mb-2`}>ICS 230 - Daily Meeting Schedule</h2>
            <p className={`${theme.colors.textMuted}`}>
              Manage daily meeting schedules and operational planning activities.
            </p>
          </div>
          <div className="flex gap-2 items-center">
            {/* List / Calendar toggle */}
            <div className="flex items-center gap-1 bg-gray-800/60 p-1 rounded-md" data-testid="ics230-listmode-toggle">
              <button
                type="button"
                onClick={() => setListMode('list')}
                className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded transition-colors ${
                  listMode === 'list'
                    ? 'bg-orange-500/20 text-orange-300 border border-orange-500/40'
                    : 'text-gray-400 hover:text-gray-200'
                }`}
                data-testid="ics230-listmode-list-btn"
              >
                <List className="h-3.5 w-3.5" />
                List
              </button>
              <button
                type="button"
                onClick={() => setListMode('calendar')}
                className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded transition-colors ${
                  listMode === 'calendar'
                    ? 'bg-orange-500/20 text-orange-300 border border-orange-500/40'
                    : 'text-gray-400 hover:text-gray-200'
                }`}
                data-testid="ics230-listmode-calendar-btn"
              >
                <CalendarDays className="h-3.5 w-3.5" />
                Calendar
              </button>
            </div>
            {schedules.length > 0 && (
              <>
                <Button
                  onClick={() => downloadIcal(null)}
                  variant="outline"
                  className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                  data-testid="ics230-export-all-ical-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export to Calendar
                </Button>
                <Button
                  onClick={printAllSchedules}
                  variant="outline"
                  className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Print All ICS 230
                </Button>
              </>
            )}
          </div>
        </div>

        {listMode === 'calendar' ? (
          <MeetingCalendarView
            schedules={schedules}
            onSelectSlot={handleCalendarSelectSlot}
            onSelectEvent={handleCalendarSelectEvent}
            onEventDrop={handleCalendarEventDrop}
          />
        ) : schedules.length === 0 ? (
          <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Calendar className={`h-12 w-12 ${theme.colors.textMuted} mb-4`} />
              <p className={`${theme.colors.textMuted} text-lg mb-4`}>No meeting schedules yet</p>
              <Button
                onClick={() => setView('add')}
                className="bg-orange-600 hover:bg-orange-700"
              >
                Create First ICS 230
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {schedules.map((schedule) => (
              <Card key={schedule.id} className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className={`font-semibold ${theme.colors.textPrimary}`}>
                        {schedule.exercise_name || 'Meeting Schedule'}
                      </h3>
                      <p className={`text-sm ${theme.colors.textMuted}`}>
                        Period: {schedule.operational_period_from || 'Not set'} - {schedule.operational_period_to || 'Not set'}
                      </p>
                      <p className={`text-sm ${theme.colors.textMuted}`}>
                        Meetings: {schedule.meetings?.length || 0}
                      </p>
                      <p className={`text-sm ${theme.colors.textMuted}`}>
                        Created: {new Date(schedule.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(schedule)}
                        className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadIcal(schedule.id)}
                        className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                        data-testid={`ics230-export-ical-${schedule.id}`}
                      >
                        <Download className="h-3 w-3 mr-1" />
                        .ics
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => printICS230(schedule)}
                        className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                      >
                        <Printer className="h-3 w-3 mr-1" />
                        Print
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(schedule.id)}
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative flex h-full">
      {/* Toast notification */}
      {toast && (
        <div
          className={`fixed top-4 z-50 px-4 py-3 rounded-lg shadow-lg border backdrop-blur-sm transition-all animate-in slide-in-from-top-2 ${
            sidebarOpen ? 'right-[22rem]' : 'right-4'
          } ${
            toast.type === 'error'
              ? 'bg-red-900/90 border-red-700 text-red-100'
              : 'bg-green-900/90 border-green-700 text-green-100'
          }`}
          data-testid="ics230-assign-toast"
        >
          <div className="flex items-center gap-2">
            {toast.type === 'error' ? <X className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
            <span className="text-sm font-medium">{toast.message}</span>
          </div>
        </div>
      )}

      <div className={`flex-1 p-6 transition-all duration-300 ${sidebarOpen ? 'mr-80' : ''}`}>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className={`text-2xl font-bold ${theme.colors.textPrimary} mb-2`}>
            {editingSchedule ? 'Edit' : 'Create'} ICS 230 - Daily Meeting Schedule
          </h2>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className={`border-blue-500/40 text-blue-400 hover:bg-blue-500/10 ${sidebarOpen ? 'bg-blue-500/10' : ''}`}
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="toggle-ics230-quick-assign-btn"
          >
            {sidebarOpen ? <PanelRightClose className="h-4 w-4 mr-2" /> : <PanelRightOpen className="h-4 w-4 mr-2" />}
            Quick Assign
          </Button>
          <Button
            onClick={printICS230}
            variant="outline"
            className="border-green-500/50 text-green-400 hover:bg-green-500/10"
          >
            <Printer className="h-4 w-4 mr-2" />
            Print ICS 230
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header Information */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div>
                <Label className="text-white">Exercise Name</Label>
                <Input
                  value={formData.exercise_name}
                  onChange={(e) => handleInputChange('exercise_name', e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                  readOnly
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-white">Operational Period</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-400 text-sm">From</Label>
                    <Input
                      type="date"
                      value={formData.operational_period_from}
                      onChange={(e) => handleInputChange('operational_period_from', e.target.value)}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-sm">To</Label>
                    <Input
                      type="date"
                      value={formData.operational_period_to}
                      onChange={(e) => handleInputChange('operational_period_to', e.target.value)}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Meeting Schedule */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-orange-500">Meeting Schedule</CardTitle>
            <Button
              onClick={addMeeting}
              size="sm"
              className="bg-orange-600 hover:bg-orange-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Meeting
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {formData.meetings.map((meeting, index) => {
              const isDropTarget = dropTargetIndex === index;
              return (
              <div
                key={meeting.id}
                data-meeting-id={meeting.id}
                className={`p-4 border rounded-lg bg-gray-900/50 transition-all duration-200 border-l-4 ${
                  isDropTarget ? 'border-blue-500 ring-2 ring-blue-500/30 scale-[1.005]' : 'border-gray-700'
                }`}
                style={getCategory(meeting.category).hex ? { borderLeftColor: getCategory(meeting.category).hex } : undefined}
                onDragOver={(e) => onDragOver(e, index)}
                onDragLeave={onDragLeave}
                onDrop={(e) => onDrop(e, index)}
                data-testid={`ics230-meeting-card-${index}`}
              >
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-white font-semibold">Meeting {index + 1}</h4>
                  <div className="flex items-center gap-2">
                    {googleCalendarLinkForMeeting(meeting) && (
                      <a
                        href={googleCalendarLinkForMeeting(meeting)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded border border-purple-500/40 text-purple-300 hover:bg-purple-500/10 transition-colors"
                        data-testid={`ics230-google-cal-meeting-${index}`}
                        title="Add this meeting to Google Calendar"
                      >
                        <CalendarDays className="h-3 w-3" />
                        Add to Google Calendar
                      </a>
                    )}
                    {formData.meetings.length > 1 && (
                      <Button
                        onClick={() => removeMeeting(index)}
                        size="sm"
                        variant="outline"
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label className="text-white">Date/Time From</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        type="date"
                        value={meeting.date_time ? meeting.date_time.split('T')[0] : ''}
                        onChange={(e) => {
                          const date = e.target.value;
                          const time = meeting.date_time ? meeting.date_time.split('T')[1] : '00:00';
                          handleMeetingChange(index, 'date_time', `${date}T${time}`);
                        }}
                        className="bg-gray-800 border-gray-700 text-white"
                        data-testid={`ics230-meeting-from-date-${index}`}
                      />
                      <Input
                        type="text"
                        placeholder="HH:MM"
                        pattern="[0-2][0-9]:[0-5][0-9]"
                        maxLength="5"
                        value={meeting.date_time ? meeting.date_time.split('T')[1] : ''}
                        onChange={(e) => {
                          let value = e.target.value.replace(/[^0-9:]/g, '');
                          if (value.length === 2 && !value.includes(':')) {
                            value = value + ':';
                          }
                          if (value.length <= 5) {
                            const date = meeting.date_time ? meeting.date_time.split('T')[0] : '';
                            handleMeetingChange(index, 'date_time', `${date}T${value}`);
                          }
                        }}
                        className="bg-gray-800 border-gray-700 text-white"
                        data-testid={`ics230-meeting-from-time-${index}`}
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-white">Date/Time To</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        type="date"
                        value={meeting.date_time_to ? meeting.date_time_to.split('T')[0] : ''}
                        onChange={(e) => {
                          const date = e.target.value;
                          const time = meeting.date_time_to ? meeting.date_time_to.split('T')[1] : '00:00';
                          handleMeetingChange(index, 'date_time_to', `${date}T${time}`);
                        }}
                        className="bg-gray-800 border-gray-700 text-white"
                        data-testid={`ics230-meeting-to-date-${index}`}
                      />
                      <Input
                        type="text"
                        placeholder="HH:MM"
                        pattern="[0-2][0-9]:[0-5][0-9]"
                        maxLength="5"
                        value={meeting.date_time_to ? meeting.date_time_to.split('T')[1] : ''}
                        onChange={(e) => {
                          let value = e.target.value.replace(/[^0-9:]/g, '');
                          if (value.length === 2 && !value.includes(':')) {
                            value = value + ':';
                          }
                          if (value.length <= 5) {
                            const date = meeting.date_time_to ? meeting.date_time_to.split('T')[0] : '';
                            handleMeetingChange(index, 'date_time_to', `${date}T${value}`);
                          }
                        }}
                        className="bg-gray-800 border-gray-700 text-white"
                        data-testid={`ics230-meeting-to-time-${index}`}
                      />
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <Label className="text-white">Meeting Name</Label>
                  <Input
                    value={meeting.meeting_name}
                    onChange={(e) => handleMeetingChange(index, 'meeting_name', e.target.value)}
                    placeholder="Enter meeting name"
                    className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label className="text-white">Purpose</Label>
                    <Textarea
                      value={meeting.purpose}
                      onChange={(e) => handleMeetingChange(index, 'purpose', e.target.value)}
                      placeholder="Enter meeting purpose"
                      className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label className="text-white">Attendees</Label>
                    {(() => {
                      const items = (meeting.attendees || '')
                        .split(/\r?\n|,/)
                        .map(s => s.trim())
                        .filter(Boolean);
                      if (items.length === 0) return null;
                      const removeAttendee = (idx) => {
                        const next = items.filter((_, i) => i !== idx).join('\n');
                        handleMeetingChange(index, 'attendees', next);
                      };
                      return (
                        <div
                          className="flex flex-wrap gap-1.5 mb-2 p-2 bg-gray-900/40 border border-gray-700 rounded-md"
                          data-testid={`ics230-attendees-chips-${index}`}
                        >
                          {items.map((name, idx) => (
                            <span
                              key={`${name}-${idx}`}
                              className="inline-flex items-center gap-1 bg-blue-500/15 border border-blue-500/30 text-blue-200 text-xs px-2 py-1 rounded-md"
                              data-testid={`ics230-attendee-chip-${index}-${idx}`}
                            >
                              {name}
                              <button
                                type="button"
                                onClick={() => removeAttendee(idx)}
                                className="text-blue-300/70 hover:text-red-400 transition-colors"
                                title="Remove"
                                data-testid={`ics230-remove-attendee-${index}-${idx}`}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      );
                    })()}
                    <Textarea
                      value={meeting.attendees}
                      onChange={(e) => handleMeetingChange(index, 'attendees', e.target.value)}
                      placeholder="List expected attendees (one per line)"
                      className="bg-gray-800 border-gray-700 text-white placeholder-gray-400 resize-none overflow-hidden"
                      rows={Math.max(3, ((meeting.attendees || '').split(/\r?\n/).length) + 1)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white">Location</Label>
                    <Input
                      value={meeting.location}
                      onChange={(e) => handleMeetingChange(index, 'location', e.target.value)}
                      placeholder="Enter meeting location"
                      className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                    />
                  </div>
                  <div>
                    <Label className="text-white">Category / Color</Label>
                    <Select
                      value={meeting.category || ''}
                      onValueChange={(val) => handleMeetingChange(index, 'category', val === '__none__' ? '' : val)}
                    >
                      <SelectTrigger
                        className="bg-gray-800 border-gray-700 text-white"
                        data-testid={`ics230-category-select-${index}`}
                      >
                        <SelectValue placeholder="None">
                          <span className="inline-flex items-center gap-2">
                            <span
                              className={`inline-block h-3 w-3 rounded-full ${getCategory(meeting.category).dotClass}`}
                              style={getCategory(meeting.category).hex ? { backgroundColor: getCategory(meeting.category).hex } : undefined}
                            />
                            {getCategory(meeting.category).label}
                          </span>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700">
                        {MEETING_CATEGORIES.map(cat => (
                          <SelectItem
                            key={cat.id || 'none'}
                            value={cat.id || '__none__'}
                            className="text-white focus:bg-gray-700 focus:text-white"
                            data-testid={`ics230-category-option-${cat.id || 'none'}`}
                          >
                            <span className="inline-flex items-center gap-2">
                              <span
                                className={`inline-block h-3 w-3 rounded-full ${cat.dotClass}`}
                                style={cat.hex ? { backgroundColor: cat.hex } : undefined}
                              />
                              {cat.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Drop hint */}
                {sidebarOpen && isDropTarget && (
                  <div className="mt-3 border-2 border-dashed border-blue-500/60 rounded-lg p-3 bg-blue-500/5 text-center">
                    <UserPlus className="h-5 w-5 text-blue-400 mx-auto mb-1" />
                    <p className="text-blue-400 text-xs font-medium">Drop personnel or resource to add to Attendees</p>
                  </div>
                )}
              </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Prepared By Section */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-orange-500">Prepared By</CardTitle>
            <Button onClick={saveEntry} disabled={loading} className="bg-orange-500 hover:bg-orange-600" size="sm">
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white">Prepared by</Label>
                <Input
                  value={formData.prepared_by}
                  onChange={(e) => handleInputChange('prepared_by', e.target.value)}
                  placeholder="Enter preparer name"
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                />
              </div>
              <div>
                <Label className="text-white">Date/Time</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    value={formData.prepared_date_time ? formData.prepared_date_time.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.prepared_date_time ? formData.prepared_date_time.split('T')[1] : '00:00';
                      handleInputChange('prepared_date_time', `${date}T${time}`);
                    }}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                  <Input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.prepared_date_time ? formData.prepared_date_time.split('T')[1] : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        const date = formData.prepared_date_time ? formData.prepared_date_time.split('T')[0] : '';
                        handleInputChange('prepared_date_time', `${date}T${value}`);
                      }
                    }}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setView('list');
              setEditingSchedule(null);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className="bg-orange-600 hover:bg-orange-700"
          >
            <Save className="h-4 w-4 mr-2" />
            {editingSchedule ? 'Update' : 'Save'} ICS 230
          </Button>
        </div>
      </div>
      </div>

      {/* Quick Assign Sidebar */}
      <div
        className={`fixed right-0 top-0 h-full w-80 bg-gray-900 border-l border-gray-700 shadow-2xl z-40 transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-testid="ics230-quick-assign-sidebar"
      >
        <div className="flex flex-col h-full">
          <div className="p-4 border-b border-gray-700 bg-gray-900/95 backdrop-blur-sm">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <UserPlus className="h-4 w-4 text-blue-500" />
                Quick Assign
              </h3>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-gray-400 hover:text-white"
                onClick={() => setSidebarOpen(false)}
                data-testid="close-ics230-quick-assign-btn"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-gray-500 text-xs mb-3">Drag personnel or resources onto a Meeting card to add to Attendees.</p>

            {/* Tabs */}
            <div className="grid grid-cols-2 gap-1 bg-gray-800/60 p-1 rounded-md mb-3">
              <button
                type="button"
                onClick={() => setSidebarTab('personnel')}
                className={`flex items-center justify-center gap-1.5 text-xs font-medium py-1.5 rounded transition-colors ${
                  sidebarTab === 'personnel'
                    ? 'bg-blue-500/20 text-blue-300 border border-blue-500/40'
                    : 'text-gray-400 hover:text-gray-200'
                }`}
                data-testid="ics230-tab-personnel"
              >
                <Users className="h-3.5 w-3.5" />
                Personnel ({participants.length})
              </button>
              <button
                type="button"
                onClick={() => setSidebarTab('resources')}
                className={`flex items-center justify-center gap-1.5 text-xs font-medium py-1.5 rounded transition-colors ${
                  sidebarTab === 'resources'
                    ? 'bg-teal-500/20 text-teal-300 border border-teal-500/40'
                    : 'text-gray-400 hover:text-gray-200'
                }`}
                data-testid="ics230-tab-resources"
              >
                <Package className="h-3.5 w-3.5" />
                Resources ({resources.length})
              </button>
            </div>

            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-500" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={sidebarTab === 'personnel' ? 'Search personnel...' : 'Search resources...'}
                className="pl-8 bg-gray-800 border-gray-600 text-white text-sm h-8"
                data-testid="ics230-quick-assign-search"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {sidebarTab === 'personnel' ? (
              <div className="p-3">
                <p className="text-blue-500/70 text-xs font-medium uppercase tracking-wider mb-2">
                  Personnel ({filteredParticipants.length})
                </p>
                {filteredParticipants.length === 0 ? (
                  <div className="text-center py-6">
                    <Users className="h-8 w-8 text-gray-600 mx-auto mb-2" />
                    <p className="text-gray-500 text-xs">
                      {searchQuery
                        ? 'No matches'
                        : participants.length === 0
                          ? 'No personnel in this exercise'
                          : 'No personnel'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {filteredParticipants.map(p => {
                      const displayName = getParticipantName(p);
                      return (
                        <div
                          key={p.id}
                          draggable
                          onDragStart={(e) => onDragStart(e, p, 'personnel')}
                          className="flex items-center gap-2 px-2.5 py-2 rounded-md bg-gray-800/50 border border-gray-700/50 cursor-grab active:cursor-grabbing hover:border-blue-500/30 hover:bg-gray-800 transition-colors group"
                          data-testid={`ics230-draggable-personnel-${p.id}`}
                        >
                          <GripVertical className="h-3.5 w-3.5 text-gray-600 group-hover:text-blue-500/60 flex-shrink-0" />
                          <Users className="h-3.5 w-3.5 text-blue-500/40 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-white truncate">{displayName}</p>
                            {(p.position || p.role) && (
                              <p className="text-xs text-gray-500 truncate">{p.position || p.role}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div className="p-3">
                <p className="text-teal-500/70 text-xs font-medium uppercase tracking-wider mb-2">
                  Resources ({filteredResources.length})
                </p>
                {filteredResources.length === 0 ? (
                  <div className="text-center py-6">
                    <Package className="h-8 w-8 text-gray-600 mx-auto mb-2" />
                    <p className="text-gray-500 text-xs">
                      {searchQuery
                        ? 'No matches'
                        : resources.length === 0
                          ? 'No resource units in this exercise'
                          : 'No resources'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {filteredResources.map(r => (
                      <div
                        key={r.id}
                        draggable
                        onDragStart={(e) => onDragStart(e, r, 'resource')}
                        className="flex items-center gap-2 px-2.5 py-2 rounded-md bg-gray-800/50 border border-gray-700/50 cursor-grab active:cursor-grabbing hover:border-teal-500/30 hover:bg-gray-800 transition-colors group"
                        data-testid={`ics230-draggable-resource-${r.id}`}
                      >
                        <GripVertical className="h-3.5 w-3.5 text-gray-600 group-hover:text-teal-500/60 flex-shrink-0" />
                        <Package className="h-3.5 w-3.5 text-teal-500/40 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-white truncate">{r.unit_name || 'Unnamed Unit'}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {r.resource_type || ''}{r.status ? ` • ${r.status}` : ''}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="p-3 border-t border-gray-700 bg-gray-900/95">
            <div className="flex justify-between text-xs text-gray-500">
              <span>{participants.length} personnel · {resources.length} units</span>
              <span>{formData.meetings.length} meeting{formData.meetings.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ICS232Form - Resources at Risk Summary

export default ICS230Form;
